// src/pages/gt3x/Reports.jsx
import { useEffect, useMemo, useState } from "react";
import RaceSessionSelect from "../../components/RaceSessionSelect";
import { safeFetchSheet } from "../../lib/sheetsSafe";
import { getAutoRaceFromSchedule } from "../../lib/autoRaceFromSchedule";

const SCHEDULE_RANGE = "Schedule!A1:D";

// Wir holen immer den kompletten Bereich, weil wir für REKO aus beiden Blöcken lesen
function rangeFor(race) {
    return `R${race}_INCIDENTS!A1:Z300`;
}

// Spaltenhilfen: A -> 0, B -> 1, ..., Z -> 25
function colIndexFromLetters(letters) {
    let n = 0;
    const s = String(letters).toUpperCase().trim();
    for (let i = 0; i < s.length; i++) {
        const code = s.charCodeAt(i);
        if (code < 65 || code > 90) continue; // nur A-Z
        n = n * 26 + (code - 64);
    }
    return n - 1; // 0-based
}

// Subset: bestimmte Spalten aus Header + Zeilen herausziehen
function subsetByLetters(header, rows, letters) {
    const indices = letters.map(colIndexFromLetters);
    const safeHeader = Array.isArray(header) ? header : [];

    // Header: wenn an der Stelle nichts steht, ersatzweise den Spaltenbuchstaben verwenden
    const subHeader = indices.map((idx, k) => {
        const cell = safeHeader[idx];
        return cell !== undefined && cell !== null && String(cell).trim() !== ""
            ? cell
            : letters[k];
    });

    const safeRows = Array.isArray(rows) ? rows : [];

    // Rows: falls eine Zeile kürzer ist, einfach leeren String einsetzen
    const subRows = safeRows.map((row) =>
        indices.map((idx) => (row && row[idx] !== undefined ? row[idx] : ""))
    );

    return { header: subHeader, rows: subRows };
}

// Führende komplett leere Zeilen entfernen
function trimLeadingEmptyRows(rows) {
    if (!Array.isArray(rows)) return [];
    let start = 0;
    while (start < rows.length) {
        const r = rows[start] || [];
        const hasValue = r.some((cell) => cell !== null && cell !== undefined && String(cell).trim() !== "");
        if (hasValue) break;
        start++;
    }
    return rows.slice(start);
}

// Einfache Table-Komponente mit Placeholder, wenn keine Rows
function SimpleTable({ header, rows, emptyMessage, highlightCols, wrapCols, colWidths }) {
    if (!header || header.length === 0) return null;

    const hasRows = Array.isArray(rows) && rows.length > 0;
    const highlightSet = Array.isArray(highlightCols) ? new Set(highlightCols) : null;
    const wrapSet = Array.isArray(wrapCols) ? new Set(wrapCols) : null;

    return (
        <div className="fancyTable">
            <table className="tbl">
                {Array.isArray(colWidths) && colWidths.length ? (
                    <colgroup>
                        {header.map((_, i) => (
                            <col
                                key={i}
                                style={colWidths[i] ? { width: colWidths[i] } : undefined}
                            />
                        ))}
                    </colgroup>
                ) : null}

                <thead>
                <tr>
                    {header.map((h, i) => (
                        // ✅ Header niemals wrap-cell geben, sonst Buchstaben-Umbruch
                        <th key={i}>
                            {h}
                        </th>
                    ))}
                </tr>
                </thead>

                <tbody>
                {hasRows ? (
                    rows.map((r, ri) => (
                        <tr key={ri}>
                            {r.map((cell, ci) => {
                                const numeric = typeof cell === "number" || !isNaN(Number(cell));
                                const numVal = Number(cell);
                                const shouldHighlight =
                                    highlightSet && highlightSet.has(ci) && numeric && numVal > 0;

                                return (
                                    <td
                                        key={ci}
                                        className={[
                                            numeric ? "num" : "",
                                            wrapSet?.has(ci) ? "wrap-cell" : "",
                                        ].join(" ").trim()}
                                        style={shouldHighlight ? { color: "#ff6b6b", fontWeight: 600 } : undefined}
                                    >
                                        {cell}
                                    </td>
                                );
                            })}
                        </tr>
                    ))
                ) : (
                    <tr>
                        <td colSpan={header.length} className="align-left text-dim">
                            {emptyMessage || "Noch keine Einträge vorhanden."}
                        </td>
                    </tr>
                )}
                </tbody>
            </table>
        </div>
    );
}


export default function Reports() {
    const [race, setRace] = useState(null);
    const [raceTouched, setRaceTouched] = useState(false);

    const [view, setView] = useState("INCIDENTS"); // "INCIDENTS" | "PROTESTS" | "REKO"

    const [data, setData] = useState({ header: [], rows: [] });
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    // ✅ Auto-Default über ResultsFrom (Schedule Spalte D)
    useEffect(() => {
        (async () => {
            try {
                const auto = await getAutoRaceFromSchedule(
                    // getAutoRaceFromSchedule erwartet fetch fn als 1. Param
                    async ({ range }) => {
                        // safeFetchSheet liefert {ok,data} – wir brauchen aber res.data im Format von normalize
                        const r = await safeFetchSheet(range);
                        if (!r.ok) throw new Error(r.message || "Schedule fetch failed");
                        return r.data;
                    },
                    SCHEDULE_RANGE,
                    "results"
                );

                if (!raceTouched) setRace(auto);
            } catch (e) {
                console.warn("Schedule auto race failed (reports)", e);
                if (!raceTouched) setRace(1);
            }
        })();
    }, [raceTouched]);

    useEffect(() => {
        if (!race) return;

        (async () => {
            setLoading(true);
            setErr("");

            const range = rangeFor(race);
            const res = await safeFetchSheet(range);

            if (!res.ok) {
                setErr(res.message || "Fehler beim Laden.");
                setData({ header: [], rows: [] });
                setLoading(false);
                return;
            }

            const d = res.data;
            let header = [];
            let rows = [];

            if (Array.isArray(d?.header) && Array.isArray(d?.rows)) {
                header = d.header;
                if (Array.isArray(d.rows[0])) rows = d.rows;
                else rows = d.rows.map((rowObj) => header.map((h) => rowObj[h]));
            } else if (Array.isArray(d?.values) && d.values.length) {
                const [head, ...body] = d.values;
                header = head;
                rows = body;
            } else {
                header = [];
                rows = [];
            }

            setData({ header, rows });
            setLoading(false);
        })();
    }, [race]);

    const incidentsTable = useMemo(() => {
        if (view !== "INCIDENTS") return null;
        const header = data.header ?? [];
        const rows = data.rows ?? [];
        return subsetByLetters(header, rows, ["A", "B", "C", "D", "E"]);
    }, [view, data.header, data.rows]);

    const protestsTable = useMemo(() => {
        if (view !== "PROTESTS") return null;

        // PROTESTE: M..S
        const subset = subsetByLetters(data.header ?? [], data.rows ?? [], ["M", "N", "O", "P", "Q", "R", "S"]);
        const trimmed = trimLeadingEmptyRows(subset.rows);

        // ✅ Header N (Index 1 im Subset) umbenennen
        const newHeader = [...subset.header];
        const h1 = String(newHeader[1] ?? "").trim().toLowerCase();
        if (h1.includes("zeitstempel") || h1.includes("timestamp")) {
            newHeader[1] = "Timestamp";
        } else {
            // falls Header leer/anders: trotzdem Index 1 als Timestamp setzen (weil du genau das willst)
            newHeader[1] = "Timestamp";
        }

        return { header: newHeader, rows: trimmed };
    }, [view, data.header, data.rows]);

    const rekoTableIncidents = useMemo(() => {
        if (view !== "REKO") return null;
        const header = data.header ?? [];
        const rows = data.rows ?? [];
        const subset = subsetByLetters(header, rows, ["A", "F", "G", "H", "I", "J"]);
        return { header: subset.header, rows: trimLeadingEmptyRows(subset.rows) };
    }, [view, data.header, data.rows]);

    const rekoTableProtests = useMemo(() => {
        if (view !== "REKO") return null;
        const header = data.header ?? [];
        const rows = data.rows ?? [];
        const subset = subsetByLetters(header, rows, ["M", "T", "U", "V", "W", "X"]);
        return { header: subset.header, rows: trimLeadingEmptyRows(subset.rows) };
    }, [view, data.header, data.rows]);

    const viewLabel =
        view === "INCIDENTS" ? "Incidents" : view === "PROTESTS" ? "Protests" : "ReKo Entscheidungen";

    return (
        <>
            <RaceSessionSelect
                valueRace={race}
                onRaceChange={setRace}
                onUserRaceChange={() => setRaceTouched(true)}
                showSession={false}
            />

            <div
                className="card"
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                }}
            >
                <div>
                    <h2>Rennkommission</h2>
                    <p className="muted">
                        <strong>{viewLabel}</strong>
                    </p>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                    <button
                        className={`button ${view === "INCIDENTS" ? "button--primary" : "button--secondary"}`}
                        onClick={() => setView("INCIDENTS")}
                    >
                        Incidents
                    </button>
                    <button
                        className={`button ${view === "PROTESTS" ? "button--primary" : "button--secondary"}`}
                        onClick={() => setView("PROTESTS")}
                    >
                        Proteste
                    </button>
                    <button
                        className={`button ${view === "REKO" ? "button--primary" : "button--secondary"}`}
                        onClick={() => setView("REKO")}
                    >
                        Reko-Entscheidungen
                    </button>
                </div>
            </div>

            {err && (
                <div className="card">
                    <p className="muted">{err}</p>
                </div>
            )}
            {loading && (
                <div className="card">
                    <p>Lade…</p>
                </div>
            )}

            {!loading && !err && (
                <>
                    {view === "INCIDENTS" && incidentsTable && (
                        <div className="reports-table">
                            <SimpleTable
                                header={incidentsTable.header}
                                rows={incidentsTable.rows}
                                emptyMessage="Noch keine Incidents eingetragen."
                            />
                        </div>
                    )}

                    {view === "PROTESTS" && protestsTable && (
                        <div className="reports-table reports-table--protests">
                            <SimpleTable
                                header={protestsTable.header}
                                rows={protestsTable.rows}
                                emptyMessage="Noch keine Fahrerproteste eingereicht."
                                wrapCols={[6]} // Beschreibung Protest
                                colWidths={[
                                    "100px", // Protest-ID
                                    "110px", // Timestamp
                                    "70px",  // #Car1
                                    "220px", // Name Car 1
                                    "70px",  // #Car2
                                    "220px", // Name Car 2
                                    "min(60vw, 980px)", // Beschreibung Protest -> richtig breit
                                ]}
                            />
                        </div>
                    )}

                    {view === "REKO" && (
                        <>
                            <div className="card">
                                <h3>ReKo Entscheidungen zu Incident Reports</h3>
                            </div>
                            <div className="reports-table reports-table--reko-incidents">
                                {rekoTableIncidents && (
                                    <SimpleTable
                                        header={rekoTableIncidents.header}
                                        rows={rekoTableIncidents.rows}
                                        emptyMessage="Noch keine ReKo-Entscheidungen zu Incident Reports."
                                        highlightCols={[3]}
                                        wrapCols={[1]} // ✅ 2. Spalte = Begründung
                                        colWidths={[
                                            "110px",            // ID
                                            "min(55vw, 900px)", // ✅ Begründung groß
                                            "150px",
                                            "150px",
                                            "150px",
                                            "150px",
                                        ]}
                                    />
                                )}
                            </div>

                            <div className="card" style={{ marginTop: 30 }}>
                                <h3>ReKo Entscheidungen zu Fahrerprotesten</h3>
                            </div>
                            <div className="reports-table reports-table--reko-protests">
                                {rekoTableProtests && (
                                    <SimpleTable
                                        header={rekoTableProtests.header}
                                        rows={rekoTableProtests.rows}
                                        emptyMessage="Noch keine ReKo-Entscheidungen zu Fahrerprotesten."
                                        highlightCols={[3]}
                                        wrapCols={[1]} // ✅ 2. Spalte = Begründung
                                        colWidths={[
                                            "140px",
                                            "min(55vw, 900px)", // ✅ Begründung groß
                                            "150px",
                                            "150px",
                                            "150px",
                                            "150px",
                                        ]}
                                    />
                                )}
                            </div>
                        </>
                    )}
                </>
            )}
        </>
    );
}
